import axios, { AxiosError } from 'axios'

const errors: Record<string, string> = {
  'MD-CORE-0001':
    'Deckenelement muss mindestens zwei Auflager (z.B. Wände oder Unterzüge) orthogonal zur Spannrichtung haben.',
  'MD-CORE-0002':
    'Unterzug muss mindestens zwei orthongonale Auflager (z.B. Wände oder Unterzüge) haben.',
  'MD-CORE-0004':
    'Nicht genug Pfetten vorhanden, auf denen das eingezeichnete Dach aufliegen kann. Es werden mindestens 2 benötigt.',
  'MD-CORE-0005': 'Es konnten nicht genug Auflager gefunden werden.',
  'MD-CORE-0006': 'Ausgewähltes Element nicht kompatibel als Auflager',
  'MD-CORE-0007':
    'Das gefundene Auflager konfligiert mit bestehenden Lagern. Gegebenenfalls muss zuerst ein Auflager entfernt werden, bevor ein neues hinzugefügt werden kann',
  'MD-CORE-0008': 'Es wurde keine Verbindung zum Zielelement gefunden',
  'MD-CORE-0016':
    'Fehler beim Löschen der Wandrippe. Es können nur selbst hinzugefügte Rippen gelöscht werden.',
  'MD-CORE-0018':
    'Fehler bei der Anpassung der Gruppierung. Gegebenenfalls muss das Element von der Gruppierung entfernt werden..',
  'MD-CORE-0019': 'Die Kurzbezeichnung wird bereits von einem anderen Aufbau verwendet.',
  'MD-CORE-0020':
    'Für diesen Aufbau konnte keine freie Kurzbezeichnung gefunden werden. Bitte eine eigene vergeben.',
  'MD-CORE-0021':
    'Die Datei wird noch im Dokument verwendet und kann nicht gelöscht werden. Bitte zuerst von diesen Seiten entfernen',
  'MD-CORE-0022': 'Die Kurzbezeichnung wird bereits von einem anderen Verbinder verwendet.',
  retriesExhausted: 'Maximale Anzahl der Versuche überschritten.',
}

// FastAPI's default validation-error `detail` is a list of {loc, msg, type}
// objects rather than a string, which would otherwise interpolate as
// "[object Object]".
const formatErrorDetail = (detail: unknown): string => {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map(item =>
        item && typeof item === 'object' && 'msg' in item
          ? [(item as { loc?: unknown[] }).loc?.join('.'), (item as { msg: string }).msg]
              .filter(Boolean)
              .join(': ')
          : JSON.stringify(item),
      )
      .join('; ')
  }
  return JSON.stringify(detail)
}

const buildErrorMessage = (error: Error | AxiosError, alternativeMessage: string) => {
  if (axios.isAxiosError(error)) {
    if (error.response?.data.code) {
      // if a code is provided, a detail is always provided as well
      const { code, detail } = error.response.data
      if (errors[code]) {
        return `${errors[code]} - Detail: ${formatErrorDetail(detail)} - Code: ${code}`
      } else {
        return `${alternativeMessage} - Detail: ${formatErrorDetail(detail)} - Code: ${code}`
      }
    } else if (error.response?.data.detail) {
      // sometimes, only a detail is provided
      return `${alternativeMessage} - Detail: ${formatErrorDetail(error.response?.data.detail)}`
    } else {
      // fallback message
      return `${alternativeMessage} - No detail available. This should not happen, so please notify the dev team. Also check response in dev tools for help.`
    }
  } else {
    return `${alternativeMessage} - Message: ${error.message}`
  }
}

// Machine-readable detail the backend answers with when a signed-in user has
// not been assigned to an organisation. Kept distinct from a plain 403 so the
// frontend can tell "not provisioned yet" apart from "not allowed".
const USER_NOT_ASSIGNED_TO_ORGANISATION_DETAIL = 'user_not_assigned_to_organisation'

const isUserNotAssignedToOrganisationError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === 403 &&
  error.response?.data?.detail === USER_NOT_ASSIGNED_TO_ORGANISATION_DETAIL

// Machine-readable detail the backend answers with when the caller addressed a
// project belonging to an organisation they are not a member of. Every
// project-scoped route answers this, not just the ones the UI happens to call.
const PROJECT_NOT_IN_USERS_ORGANISATION_DETAIL = 'project_not_in_users_organisation'

const isProjectNotInUsersOrganisationError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === 403 &&
  error.response?.data?.detail === PROJECT_NOT_IN_USERS_ORGANISATION_DETAIL

// The active-organisation header named an organisation the caller is not a
// member of -- a choice remembered from before the membership ended, or from
// another account on the same browser.
const UNKNOWN_ACTIVE_ORGANISATION_DETAIL = 'unknown_active_organisation'

const isUnknownActiveOrganisationError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === 403 &&
  error.response?.data?.detail === UNKNOWN_ACTIVE_ORGANISATION_DETAIL

// A project file ("Dateien") cannot be deleted while document items still show it
const PROJECT_FILE_IN_USE_CODE = 'MD-CORE-0021'

const isProjectFileInUseError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === 409 &&
  error.response?.data?.code === PROJECT_FILE_IN_USE_CODE

export {
  errors,
  buildErrorMessage,
  isUserNotAssignedToOrganisationError,
  USER_NOT_ASSIGNED_TO_ORGANISATION_DETAIL,
  isProjectNotInUsersOrganisationError,
  PROJECT_NOT_IN_USERS_ORGANISATION_DETAIL,
  isUnknownActiveOrganisationError,
  UNKNOWN_ACTIVE_ORGANISATION_DETAIL,
  isProjectFileInUseError,
  PROJECT_FILE_IN_USE_CODE,
}
